import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  CustomerPoints,
  CustomerPointsEntry,
  CustomerPointsHistoryParams,
  LoyaltyBrandRule,
  LoyaltyBrandRuleListParams,
  LoyaltyRules,
  LoyaltyRulesInput,
  PointsAdjustmentInput,
  PointsAdjustmentResult,
} from '../types/loyalty.types';

/**
 * Loyalty points Admin API. The backend re-checks every country (and every customer's
 * country) against the staff member's scope, and every permission.
 */

const BASE = '/admin/loyalty';

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

function toPaged<T>(page: AdminPage<T>): PagedResponse<T> {
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

export async function getLoyaltyRules(countryId: number, signal?: AbortSignal) {
  return http.get<LoyaltyRules>(`${BASE}/countries/${countryId}/rules`, { signal });
}

/** loyalty.manage. Audited per rule that changed. */
export async function saveLoyaltyRules(countryId: number, input: LoyaltyRulesInput) {
  return http.put<{ changed: number }>(`${BASE}/countries/${countryId}/rules`, input);
}

export async function getLoyaltyBrandRules(
  countryId: number,
  params: LoyaltyBrandRuleListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<LoyaltyBrandRule>> {
  return toPaged(
    await http.get<AdminPage<LoyaltyBrandRule>>(`${BASE}/countries/${countryId}/brand-rules`, {
      params,
      signal,
    }),
  );
}

/** loyalty.manage. 409 LOYALTY_RULE_EXISTS when the brand already has a rule here. */
export async function createLoyaltyBrandRule(
  countryId: number,
  input: { brandId: number; points: number },
) {
  return http.post<LoyaltyBrandRule>(`${BASE}/countries/${countryId}/brand-rules`, input);
}

export async function updateLoyaltyBrandRule(id: number, points: number) {
  return http.put<{ changed: number }>(`${BASE}/brand-rules/${id}`, { points });
}

/** Deactivating makes the brand fall back to the country's default purchase rule. */
export async function setLoyaltyBrandRuleActive(id: number, active: boolean) {
  return http.post<{ changed: number }>(
    `${BASE}/brand-rules/${id}/${active ? 'activate' : 'deactivate'}`,
  );
}

export async function getCustomerPoints(customerId: number, signal?: AbortSignal) {
  return http.get<CustomerPoints>(`/admin/customers/${customerId}/points`, { signal });
}

export async function getCustomerPointsHistory(
  customerId: number,
  params: CustomerPointsHistoryParams,
  signal?: AbortSignal,
): Promise<PagedResponse<CustomerPointsEntry>> {
  return toPaged(
    await http.get<AdminPage<CustomerPointsEntry>>(
      `/admin/customers/${customerId}/points/history`,
      {
        params,
        signal,
      },
    ),
  );
}

/** loyalty.adjust. Reason required; audited; 409 LOYALTY_INSUFFICIENT_BALANCE below zero. */
export async function adjustCustomerPoints(customerId: number, input: PointsAdjustmentInput) {
  return http.post<PointsAdjustmentResult>(
    `/admin/customers/${customerId}/points/adjustments`,
    input,
  );
}
